<template>
  <div class="home">
    <Navbar :is-active="6"></Navbar>
    <div class="body">
      <MesasSidebar active="pos" />
      <div class="body__containt">
        <!-- ===== PLANO DE MESAS ===== -->
        <template v-if="view === 'floor'">
          <header class="pos-head">
            <div class="page__title">
              <open-arrow></open-arrow>
              <div>
                <h2>POS — Mesas</h2>
                <p class="page__lead">Elige una mesa. Verás si está libre u ocupada y quién abrió la comanda.</p>
              </div>
            </div>
            <button class="btn btn--ghost" :disabled="loadingFloor" @click="loadFloor">
              <i class="fas fa-sync-alt" :class="{ spin: loadingFloor }"></i>
              Refrescar
            </button>
          </header>

          <div class="floor-summary" v-if="floorSummary">
            <span class="sum sum--libre">{{ floorSummary.libre }} libres</span>
            <span class="sum sum--ocupada">{{ floorSummary.ocupada }} ocupadas</span>
            <span class="sum sum--parcial">{{ floorSummary.parcial }} parciales</span>
          </div>

          <div class="search-wrap search-wrap--floor">
            <span class="search-wrap__icon" aria-hidden="true">
              <i class="fas fa-search"></i>
            </span>
            <input type="search" class="pos-search" placeholder="Buscar mesa por nombre o número…" v-model="tableSearch" />
          </div>

          <div class="floor-grid" v-if="filteredFloor.length">
            <button
              v-for="t in filteredFloor"
              :key="t.id"
              type="button"
              class="floor-card"
              :class="'is-' + t.occupancy"
              @click="enterTable(t)"
            >
              <div class="floor-card__top">
                <span class="floor-card__num">{{ t.table_number }}</span>
                <span class="occ" :class="'occ--' + t.occupancy">{{ occupancyLabel(t.occupancy) }}</span>
              </div>
              <strong class="floor-card__name">{{ t.table_name }}</strong>
              <template v-if="t.order">
                <p class="floor-card__waiter">
                  <i class="fas fa-user"></i>
                  {{ (t.order.waiter && t.order.waiter.name) || 'Sin mesero' }}
                </p>
                <p class="floor-card__money">
                  Pendiente S/ {{ formatMoney(t.order.pending) }}
                  <em v-if="t.order.items_count"> · {{ t.order.items_count }} ítems</em>
                </p>
              </template>
              <p v-else class="floor-card__hint">Toca para abrir comanda</p>
            </button>
          </div>
          <div v-else class="empty-block empty-block--lg">
            <i class="fas fa-chair"></i>
            <p>{{ loadingFloor ? 'Cargando mesas…' : 'No hay mesas activas' }}</p>
          </div>
        </template>

        <!-- ===== COMANDA DE UNA MESA ===== -->
        <template v-else>
          <header class="pos-head">
            <div class="page__title">
              <open-arrow></open-arrow>
              <div>
                <button type="button" class="back-link" @click="backToFloor">
                  <i class="fas fa-arrow-left" aria-hidden="true"></i>
                  Ver todas las mesas
                </button>
                <h2>Mesa #{{ selectedTable && selectedTable.table_number }} — {{ selectedTable && selectedTable.table_name }}</h2>
                <p class="page__lead" v-if="order && order.waiter">
                  Abierta por <strong>{{ order.waiter.name }}</strong>
                  <span v-if="order.opened_at"> · {{ formatOpened(order.opened_at) }}</span>
                </p>
                <p class="page__lead" v-else>Agrega platos; se guardan al tocarlos.</p>
              </div>
            </div>
            <div class="pos-head__chips" v-if="order">
              <span class="chip chip--soft">{{ statusLabel(order.status) }}</span>
              <span class="chip chip--money">Pendiente S/ {{ formatMoney(order.totals.pending) }}</span>
            </div>
          </header>

          <section class="pos-layout">
            <section class="pos-panel pos-panel--wide">
              <div class="panel-head panel-head--actions">
                <div>
                  <h3>Comanda</h3>
                  <p class="panel-sub" v-if="order">#{{ order.id }}</p>
                  <p class="panel-sub" v-else>Sin comanda — toca un plato o ábrela</p>
                </div>
                <div class="header-actions">
                  <button
                    v-if="!order"
                    class="btn btn--primary"
                    :disabled="loadingOrder"
                    @click="openOrder"
                  >
                    {{ loadingOrder ? '…' : 'Abrir comanda' }}
                  </button>
                  <button
                    class="btn btn--ghost"
                    v-if="order && order.status !== 'closed' && order.status !== 'paid'"
                    :disabled="closing"
                    @click="closeOrder"
                  >
                    Cerrar
                  </button>
                </div>
              </div>

              <div class="split">
                <div class="split__menu">
                  <div class="split__title">
                    <h4>Menú</h4>
                    <span class="panel-count">{{ filteredMenu.length }}</span>
                  </div>
                  <div class="search-wrap">
                    <span class="search-wrap__icon" aria-hidden="true">
                      <i class="fas fa-search"></i>
                    </span>
                    <input type="search" class="pos-search" placeholder="Buscar plato…" v-model="menuSearch" />
                  </div>
                  <div class="pos-menu-grid" :class="{ dimmed: adding }">
                    <button
                      type="button"
                      class="menu-card"
                      v-for="item in filteredMenu"
                      :key="item.id"
                      :disabled="adding"
                      @click="addItem(item)"
                    >
                      <strong>{{ item.menu_name }}</strong>
                      <span>S/ {{ formatMoney(item.menu_price) }}</span>
                      <em class="menu-card__add">+ Agregar</em>
                    </button>
                    <div v-if="!filteredMenu.length" class="empty-block">
                      <i class="fas fa-utensils"></i>
                      <p>Sin productos en el menú</p>
                    </div>
                  </div>
                </div>

                <div class="split__order">
                  <div class="split__title">
                    <h4>Pedido</h4>
                    <span class="panel-count" v-if="visibleItems.length">{{ visibleItems.length }}</span>
                  </div>
                  <div class="order-items" v-if="visibleItems.length">
                    <div
                      class="order-item"
                      v-for="item in visibleItems"
                      :key="item.id"
                      :class="'is-' + item.status"
                    >
                      <div class="order-item__main">
                        <strong>{{ item.name }}</strong>
                        <span class="order-item__meta">
                          x{{ item.quantity }}
                          <span class="pill" :class="'pill--' + item.status">{{ itemStatusLabel(item.status) }}</span>
                        </span>
                      </div>
                      <div class="order-item__right">
                        <strong>S/ {{ formatMoney(item.line_total) }}</strong>
                        <button
                          v-if="item.status === 'pending'"
                          type="button"
                          class="link-btn"
                          @click="cancelItem(item)"
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-block">
                    <i class="fas fa-receipt"></i>
                    <p>Aún no hay ítems. Toca un plato del menú.</p>
                  </div>
                </div>
              </div>
            </section>

            <aside class="pos-panel pos-panel--totals">
              <div class="panel-head"><h3>Cobro estimado</h3></div>

              <div v-if="!order" class="empty-block">
                <i class="fas fa-calculator"></i>
                <p>Sin comanda activa</p>
              </div>

              <template v-else>
                <div class="totals-hero">
                  <span>Total estimado</span>
                  <strong>S/ {{ formatMoney(breakdown.total) }}</strong>
                </div>

                <div class="totals-summary">
                  <div class="totals-row">
                    <span>Pendiente</span>
                    <strong>S/ {{ formatMoney(order.totals.pending) }}</strong>
                  </div>
                  <div class="totals-row">
                    <span>Ya pagado</span>
                    <strong>S/ {{ formatMoney(order.totals.paid) }}</strong>
                  </div>
                  <div class="totals-row muted-row">
                    <span>Propina {{ tipPercentage }}%</span>
                    <strong>S/ {{ formatMoney(breakdown.tip_amount) }}</strong>
                  </div>
                  <div class="totals-row muted-row">
                    <span>Fee {{ feePercentage }}%</span>
                    <strong>S/ {{ formatMoney(breakdown.platform_fee) }}</strong>
                  </div>
                </div>

                <div class="tip-block">
                  <p class="tip-block__label">Propina rápida</p>
                  <div class="tip-chips">
                    <button
                      v-for="t in tipOptions"
                      :key="t"
                      type="button"
                      class="tip-chip"
                      :class="{ active: tipPercentage === t }"
                      @click="tipPercentage = t"
                    >
                      {{ t }}%
                    </button>
                  </div>
                </div>
              </template>
            </aside>
          </section>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Navbar from '@/components/NavBar.vue'
import MesasSidebar from '@/components/MesasSidebar.vue'
import OpenArrow from '@/components/OpenArrow.vue'

export default {
  name: 'PosMesas',
  components: { Navbar, MesasSidebar, OpenArrow },
  data() {
    return {
      view: 'floor',
      floorTables: [],
      floorSummary: null,
      loadingFloor: false,
      menuItems: [],
      selectedTableId: null,
      selectedTable: null,
      tableSearch: '',
      menuSearch: '',
      order: null,
      loadingOrder: false,
      adding: false,
      closing: false,
      tipPercentage: 10,
      tipOptions: [0, 5, 10, 15],
      igvPercentage: 18,
      feePercentage: 2.5
    }
  },
  computed: {
    companyId() {
      return parseInt(localStorage.getItem('company_id'), 10)
    },
    filteredFloor() {
      const q = (this.tableSearch || '').toLowerCase().trim()
      if (!q) return this.floorTables
      return this.floorTables.filter(function (t) {
        return String(t.table_number).includes(q) || (t.table_name || '').toLowerCase().indexOf(q) !== -1
      })
    },
    filteredMenu() {
      const q = (this.menuSearch || '').toLowerCase().trim()
      if (!q) return this.menuItems
      return this.menuItems.filter(function (m) {
        return (m.menu_name || '').toLowerCase().indexOf(q) !== -1
      })
    },
    visibleItems() {
      if (!this.order || !this.order.items) return []
      return this.order.items.filter(function (i) { return i.status !== 'cancelled' })
    },
    breakdown() {
      const gross = this.order ? Number(this.order.totals.pending || 0) : 0
      const igvRate = this.igvPercentage / 100
      const feeRate = this.feePercentage / 100
      const tipRate = Math.max(0, Number(this.tipPercentage) || 0) / 100
      const factor = 1 + igvRate
      const subtotal = igvRate > 0 ? this.round2(gross / factor) : this.round2(gross)
      const igv_amount = this.round2(gross - subtotal)
      const tip_amount = this.round2(gross * tipRate)
      const platform_fee = this.round2(gross * feeRate)
      const total = this.round2(gross + tip_amount + platform_fee)
      return { subtotal, igv_amount, tip_amount, platform_fee, total }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login')
      return
    }
    this.loadFloor()
    this.loadMenu()
  },
  methods: {
    occupancyLabel(v) {
      if (v === 'ocupada') return 'Ocupada'
      if (v === 'parcial') return 'Parcial'
      return 'Libre'
    },
    statusLabel(status) {
      const map = { open: 'Abierta', partial: 'Pago parcial', paid: 'Pagada', closed: 'Cerrada', cancelled: 'Cancelada' }
      return map[status] || status
    },
    itemStatusLabel(status) {
      const map = { pending: 'Pendiente', reserved: 'Reservado', paid: 'Pagado', cancelled: 'Cancelado' }
      return map[status] || status
    },
    formatOpened(iso) {
      try {
        const d = new Date(iso)
        return d.toLocaleString('es-PE', { dateStyle: 'short', timeStyle: 'short' })
      } catch (e) {
        return iso
      }
    },
    round2(v) {
      return Math.round((Number(v) + Number.EPSILON) * 100) / 100
    },
    formatMoney(v) {
      return this.round2(v).toFixed(2)
    },
    async loadFloor() {
      this.loadingFloor = true
      try {
        const res = await axios.get('general/table/floor', {
          params: { company_id: this.companyId }
        })
        if (res.data && res.data.success) {
          this.floorTables = (res.data.data && res.data.data.tables) || []
          this.floorSummary = (res.data.data && res.data.data.summary) || null
        } else {
          this.floorTables = []
          this.floorSummary = null
        }
      } catch (e) {
        this.floorTables = []
        this.floorSummary = null
      } finally {
        this.loadingFloor = false
      }
    },
    async loadMenu() {
      try {
        const res = await axios.get('general/menu-product', {
          params: { page: 1, menu_company_id: this.companyId, menu_status: 1 }
        })
        this.menuItems = (res.data.export || (res.data.data && res.data.data.data) || []).filter(function (m) {
          return m.menu_status === 1
        })
      } catch (e) {
        this.menuItems = []
      }
    },
    async enterTable(t) {
      this.selectedTableId = t.id
      this.selectedTable = t
      this.order = null
      this.view = 'order'
      await this.fetchOrder()
    },
    async backToFloor() {
      this.view = 'floor'
      this.selectedTableId = null
      this.selectedTable = null
      this.order = null
      await this.loadFloor()
    },
    async fetchOrder() {
      if (!this.selectedTableId) return
      try {
        const res = await axios.get('general/table/' + this.selectedTableId + '/order', {
          params: { company_id: this.companyId }
        })
        this.order = res.data.data || null
      } catch (e) {
        this.order = null
      }
    },
    async openOrder() {
      this.loadingOrder = true
      try {
        const res = await axios.post('general/table/order/open', {
          table_id: this.selectedTableId,
          company_id: this.companyId
        })
        if (res.data.success) {
          this.order = res.data.data
        } else {
          this.$swal({ icon: 'error', title: res.data.message || 'Error', timer: 2000, showConfirmButton: false })
        }
      } catch (e) {
        this.$swal({
          icon: 'error',
          title: (e.response && e.response.data && e.response.data.message) || 'Error al abrir comanda',
          timer: 2000,
          showConfirmButton: false
        })
      } finally {
        this.loadingOrder = false
      }
    },
    async addItem(menu) {
      if (!this.order) {
        await this.openOrder()
        if (!this.order) return
      }
      this.adding = true
      try {
        const res = await axios.post('general/table/order/' + this.order.id + '/items', {
          menu_product_id: menu.id,
          quantity: 1,
          company_id: this.companyId
        })
        if (res.data.success) {
          this.order = res.data.order
        } else {
          this.$swal({ icon: 'error', title: res.data.message || 'No se pudo agregar', timer: 2000, showConfirmButton: false })
        }
      } catch (e) {
        this.$swal({
          icon: 'error',
          title: (e.response && e.response.data && e.response.data.message) || 'Error',
          timer: 2000,
          showConfirmButton: false
        })
      } finally {
        this.adding = false
      }
    },
    async cancelItem(item) {
      try {
        const res = await axios.delete(
          'general/table/order/' + this.order.id + '/items/' + item.id,
          { params: { company_id: this.companyId } }
        )
        if (res.data.success) this.order = res.data.order
      } catch (e) {
        this.$swal({
          icon: 'error',
          title: (e.response && e.response.data && e.response.data.message) || 'No se pudo quitar',
          timer: 2000,
          showConfirmButton: false
        })
      }
    },
    async closeOrder() {
      this.closing = true
      try {
        const res = await axios.post('general/table/order/' + this.order.id + '/close', {
          company_id: this.companyId
        })
        if (res.data.success) {
          this.$swal({ icon: 'success', title: 'Comanda cerrada', timer: 1200, showConfirmButton: false })
          await this.backToFloor()
        } else {
          this.$swal({ icon: 'error', title: res.data.message || 'No se pudo cerrar', timer: 2500, showConfirmButton: false })
        }
      } catch (e) {
        this.$swal({
          icon: 'error',
          title: (e.response && e.response.data && e.response.data.message) || 'Error',
          timer: 2500,
          showConfirmButton: false
        })
      } finally {
        this.closing = false
      }
    },
  }
}
</script>

<style scoped>
.body { width: 100%; display: flex; }
.body__containt {
  min-height: 87vh; width: 100%; min-width: 0;
  display: flex; flex-direction: column;
  padding: 18px 24px 28px; background: #f3f4f6; box-sizing: border-box;
}
.pos-head {
  display: flex; flex-wrap: wrap; align-items: flex-start; justify-content: space-between;
  gap: 12px; margin-bottom: 14px;
}
.page__title { display: flex; align-items: flex-start; gap: 12px; }
.page__title h2 { margin: 0; font-weight: 700; color: #ff3128; font-size: 22px; }
.page__lead { margin: 4px 0 0; color: #6b7280; font-size: 13px; max-width: 480px; line-height: 1.4; }
.page__lead strong { color: #111827; }
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #e5e7eb;
  background: #fff;
  padding: 8px 12px;
  margin: 0 0 10px;
  border-radius: 999px;
  color: #374151;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
}
.back-link i {
  color: #ff3128;
  font-size: 12px;
}
.back-link:hover {
  border-color: #ffc5c2;
  background: #fff5f4;
  color: #ff3128;
}
.pos-head__chips { display: flex; flex-wrap: wrap; gap: 8px; }
.chip {
  display: inline-flex; padding: 6px 12px; border-radius: 999px; background: #fff;
  border: 1px solid #e5e7eb; font-size: 12px; font-weight: 600; color: #374151;
}
.chip--soft { background: #ffeeed; border-color: #ffd0cd; color: #ff3128; }
.chip--money { background: #ecfdf5; border-color: #a7f3d0; color: #047857; }

.floor-summary { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 12px; }
.sum {
  padding: 6px 12px; border-radius: 999px; font-size: 12px; font-weight: 700;
}
.sum--libre { background: #ecfdf5; color: #047857; }
.sum--ocupada { background: #ffeeed; color: #ff3128; }
.sum--parcial { background: #fff7ed; color: #c2410c; }

.search-wrap {
  position: relative;
  margin-bottom: 12px;
  display: block;
}
.search-wrap--floor {
  max-width: 320px;
}
.search-wrap__icon {
  position: absolute;
  left: 14px;
  top: 0;
  bottom: 0;
  width: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 13px;
  pointer-events: none;
  z-index: 1;
}
.search-wrap__icon :deep(svg),
.search-wrap__icon i {
  width: 14px;
  height: 14px;
  display: block;
}
.pos-search {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 11px 14px 11px 40px;
  font-size: 14px;
  line-height: 1.2;
  background: #fff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.pos-search::placeholder {
  color: #9ca3af;
}
.pos-search:focus {
  outline: none;
  border-color: #ff3128;
  box-shadow: 0 0 0 3px rgba(255, 49, 40, 0.12);
}
.pos-search::-webkit-search-cancel-button {
  -webkit-appearance: none;
  appearance: none;
  height: 14px;
  width: 14px;
  background: #d1d5db;
  border-radius: 50%;
  cursor: pointer;
}

.floor-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 12px;
}
.floor-card {
  text-align: left; border: 1px solid #e5e7eb; border-radius: 14px; background: #fff;
  padding: 14px; cursor: pointer;
  transition: transform 0.12s ease, border-color 0.15s ease, box-shadow 0.15s ease;
}
.floor-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.08);
}
.floor-card.is-libre { border-color: #a7f3d0; }
.floor-card.is-ocupada { border-color: #ffc5c2; background: #fffafa; }
.floor-card.is-parcial { border-color: #fdba74; background: #fffaf5; }
.floor-card__top {
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px;
}
.floor-card__num {
  width: 40px; height: 40px; border-radius: 10px; display: flex; align-items: center; justify-content: center;
  background: #111827; color: #fff; font-weight: 800; font-size: 15px;
}
.floor-card.is-libre .floor-card__num { background: #059669; }
.floor-card.is-ocupada .floor-card__num { background: #ff3128; }
.floor-card.is-parcial .floor-card__num { background: #ea580c; }
.occ {
  font-size: 11px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.03em;
  padding: 3px 8px; border-radius: 999px;
}
.occ--libre { background: #d1fae5; color: #065f46; }
.occ--ocupada { background: #fee2e2; color: #991b1b; }
.occ--parcial { background: #ffedd5; color: #9a3412; }
.floor-card__name {
  display: block; font-size: 14px; color: #111827; margin-bottom: 6px;
}
.floor-card__waiter, .floor-card__money, .floor-card__hint {
  margin: 0; font-size: 12px; color: #6b7280; line-height: 1.35;
}
.floor-card__waiter i { margin-right: 4px; font-size: 10px; }
.floor-card__money { color: #047857; font-weight: 700; margin-top: 4px; }
.floor-card__money em { font-style: normal; font-weight: 500; color: #9ca3af; }
.floor-card__hint { color: #9ca3af; }

.pos-layout {
  display: grid; grid-template-columns: minmax(0, 1fr) 280px; gap: 14px; align-items: start; flex: 1;
}
.pos-panel {
  background: #fff; border: 1px solid #e5e7eb; border-radius: 14px; padding: 14px;
  display: flex; flex-direction: column; min-width: 0; box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.pos-panel--wide { max-height: calc(100vh - 180px); }
.pos-panel--totals { position: sticky; top: 12px; }
.panel-head { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.panel-head--actions { justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 10px; }
.panel-head h3 { margin: 0; font-size: 15px; font-weight: 700; color: #111827; }
.panel-sub { margin: 2px 0 0; font-size: 12px; color: #9ca3af; }
.panel-count {
  min-width: 22px; height: 22px; padding: 0 6px; border-radius: 999px; background: #f3f4f6;
  color: #6b7280; font-size: 11px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center;
}
.header-actions { display: flex; gap: 8px; flex-wrap: wrap; }

.btn {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  border: none; border-radius: 10px; padding: 9px 14px; font-size: 13px; font-weight: 600; cursor: pointer;
}
.btn:disabled { opacity: 0.55; cursor: not-allowed; }
.btn--primary { background: #32ba7c; color: #fff; }
.btn--ghost { background: #fff; color: #ff3128; border: 1px solid #ffc5c2; }
.spin { animation: spin 0.8s linear infinite; }

.split {
  display: grid; grid-template-columns: 1.05fr 0.95fr; gap: 14px; min-height: 0; flex: 1; overflow: hidden;
}
.split__menu, .split__order { display: flex; flex-direction: column; min-height: 0; min-width: 0; }
.split__title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
.split__title h4 {
  margin: 0; font-size: 12px; font-weight: 700; color: #374151;
  text-transform: uppercase; letter-spacing: 0.03em;
}
.pos-menu-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(132px, 1fr));
  gap: 8px; overflow: auto; flex: 1;
}
.pos-menu-grid.dimmed { opacity: 0.7; pointer-events: none; }
.menu-card {
  border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px; text-align: left;
  background: #fff; cursor: pointer; display: flex; flex-direction: column; gap: 4px;
}
.menu-card:hover:not(:disabled) { border-color: #32ba7c; background: #f0fdf4; }
.menu-card strong { font-size: 13px; color: #111827; }
.menu-card span { font-size: 13px; color: #059669; font-weight: 700; }
.menu-card__add { font-style: normal; font-size: 11px; color: #9ca3af; font-weight: 600; margin-top: 2px; }

.order-items { display: flex; flex-direction: column; gap: 8px; overflow: auto; flex: 1; }
.order-item {
  display: flex; justify-content: space-between; gap: 10px;
  border: 1px solid #eee; border-radius: 12px; padding: 11px 12px; background: #fff;
}
.order-item.is-paid { opacity: 0.72; background: #f9fafb; }
.order-item.is-reserved { border-color: #fde68a; background: #fffbeb; }
.order-item__main { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.order-item__main strong { font-size: 13px; }
.order-item__meta { display: flex; align-items: center; gap: 8px; font-size: 12px; color: #6b7280; }
.order-item__right { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; }
.link-btn { background: none; border: none; color: #ff3128; cursor: pointer; font-size: 12px; font-weight: 700; padding: 0; }
.pill {
  display: inline-flex; padding: 2px 7px; border-radius: 999px; font-size: 10px; font-weight: 700; text-transform: uppercase;
}
.pill--pending { background: #fee2e2; color: #b91c1c; }
.pill--reserved { background: #fef3c7; color: #92400e; }
.pill--paid { background: #dcfce7; color: #166534; }

.totals-hero {
  display: flex; flex-direction: column; gap: 2px; padding: 14px; border-radius: 12px;
  background: #111827; color: #fff; margin-bottom: 12px;
}
.totals-hero span { font-size: 11px; text-transform: uppercase; letter-spacing: 0.04em; color: #9ca3af; }
.totals-hero strong { font-size: 24px; font-weight: 800; color: #6ee7b7; }
.totals-summary { display: flex; flex-direction: column; gap: 8px; margin-bottom: 12px; }
.totals-row { display: flex; justify-content: space-between; font-size: 13px; color: #374151; }
.muted-row { color: #9ca3af; font-size: 12px; }
.tip-block { margin-bottom: 8px; padding-top: 10px; border-top: 1px dashed #e5e7eb; }
.tip-block__label { margin: 0 0 8px; font-size: 12px; color: #6b7280; font-weight: 600; }
.tip-chips { display: flex; gap: 6px; flex-wrap: wrap; }
.tip-chip {
  border: 1px solid #e5e7eb; background: #fff; border-radius: 999px; padding: 6px 12px;
  font-size: 12px; font-weight: 700; cursor: pointer; color: #374151;
}
.tip-chip.active { background: #ff3128; border-color: #ff3128; color: #fff; }

.empty-block {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; padding: 28px 12px; color: #9ca3af; text-align: center;
}
.empty-block--lg { padding: 48px 16px; }
.empty-block i { font-size: 22px; opacity: 0.7; }
.empty-block p { margin: 0; font-size: 13px; }

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 1100px) {
  .pos-layout { grid-template-columns: 1fr; }
  .pos-panel--wide { max-height: none; }
  .split { grid-template-columns: 1fr; }
  .pos-panel--totals { position: static; }
}
</style>
